import { Head, Link } from "@inertiajs/react"
import PublicLayout from "@/components/public/layout"
import { ScrollReveal } from "@/components/ui/scroll-reveal"
import { OptimizedImage } from "@/components/ui/optimized-image"
import { VideoPhone } from "@/components/public/video-phone"
import { BreadcrumbSchema } from "@/components/public/breadcrumb-schema"
import { RelatedPages } from "@/components/public/related-pages"
import { relatedFor } from "@/data/related-pages"
import { cdnUrl } from "@/lib/cdn"
import { imageSrcSet } from "@/data/location-images"
import type { SeoMeta } from "@/types"

/* ------------------------------------------------------------------ */
/*  Data                                                               */
/* ------------------------------------------------------------------ */

type ComparisonRow = {
  label: string
  diy: string
  quiteright: string
  highlight?: boolean
}

const COMPARISON: ComparisonRow[] = [
  {
    label: "Time per video",
    diy: "1\u20134 hours",
    quiteright: "60 seconds",
    highlight: true,
  },
  {
    label: "Filming required",
    diy: "Yes, on-site with phone or camera",
    quiteright: "No filming needed",
    highlight: true,
  },
  {
    label: "Editing software",
    diy: "CapCut, iMovie, or Premiere",
    quiteright: "None required",
  },
  {
    label: "Voiceover",
    diy: "Record yourself or skip it",
    quiteright: "AI voiceover, multiple voices",
    highlight: true,
  },
  {
    label: "Script writing",
    diy: "Write your own",
    quiteright: "Auto-generated from listing",
  },
  {
    label: "Music selection",
    diy: "Find royalty-free tracks",
    quiteright: "Included, pre-selected",
  },
  {
    label: "Branding consistency",
    diy: "Depends on who edits",
    quiteright: "Automatic on every video",
  },
  {
    label: "Multiple formats",
    diy: "Re-edit for each platform",
    quiteright: "Reels, TikTok, Shorts, landscape",
  },
  {
    label: "Quality floor",
    diy: "Varies wildly",
    quiteright: "Professional, every time",
    highlight: true,
  },
  {
    label: "Scalable to 50+ listings",
    diy: "Impossible",
    quiteright: "No extra effort",
    highlight: true,
  },
]

const REALITY_CHECKS = [
  {
    myth: "\"I'll just film it on my phone.\"",
    reality: "You can. But shaky footage, bad lighting, and background noise are why 80% of DIY property videos never get published. And even when they do, they rarely look professional enough for a market appraisal presentation.",
  },
  {
    myth: "\"My negotiator can learn to edit.\"",
    reality: "They could. But their time costs more than the video. A negotiator spending 2 hours editing a video is 2 hours not spent on viewings, calls, and winning instructions. At even \u00A315/hour, that's \u00A330 of staff time per video.",
  },
  {
    myth: "\"Free editing apps are good enough.\"",
    reality: "CapCut and iMovie are capable tools. But property video marketing isn't just about editing: it's about scripts, voiceover, music licensing, branding, and multi-platform formats. DIY means solving each of these individually, every single time.",
  },
  {
    myth: "\"I'll just do it for the premium listings.\"",
    reality: "That's what everyone says. Then only 2 out of 20 listings get a video. The whole point of video marketing is reach across your portfolio. The \u00A3200k terrace needs a video just as much as the \u00A31m detached.",
  },
]

type FaqItem = { q: string; a: string }

const FAQ: FaqItem[] = [
  {
    q: "Is DIY video marketing worth the effort?",
    a: "Only if you have the time and skills. Most agents start with good intentions but stop after a few videos because the time commitment is unsustainable. The agencies that succeed with video are the ones that remove the friction, either by hiring someone or by using a tool like QuiteRight.",
  },
  {
    q: "What equipment do I need for DIY property videos?",
    a: "At minimum: a smartphone with a good camera, a gimbal for stabilisation, a clip-on microphone, and decent lighting. That\u2019s \u00A3200\u2013\u00A3500 in equipment before you even start editing. With QuiteRight, you need nothing: just paste a listing URL.",
  },
  {
    q: "How long does it really take to make a DIY property video?",
    a: "Honestly? 1\u20134 hours per listing once you factor in filming (30\u201360 minutes on-site), editing (30\u201390 minutes), adding music and text, and exporting for each social platform. Multiply that by your listings per month and the time adds up fast.",
  },
  {
    q: "Can I combine DIY filming with QuiteRight?",
    a: "Absolutely. Some agents film walkthrough clips for premium listings and use QuiteRight for everything else. It\u2019s not one or the other. The goal is to ensure every listing has video marketing, and QuiteRight makes that possible without a production budget.",
  },
  {
    q: "What if my DIY videos perform well?",
    a: "Great! Keep making them. QuiteRight isn\u2019t about replacing what works. It\u2019s about filling the gaps. If you can only DIY 3 videos a month, QuiteRight handles the other 17. Every listing deserves marketing, not just the ones you have time for.",
  },
]

/* ------------------------------------------------------------------ */
/*  Components                                                         */
/* ------------------------------------------------------------------ */

function ComparisonTable({ rows }: { rows: ComparisonRow[] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[540px] text-left">
        <thead>
          <tr className="border-b-2 border-stone">
            <th className="pb-3 pr-4 text-xs font-medium uppercase tracking-[0.15em] text-slate">
              Feature
            </th>
            <th className="pb-3 px-4 text-center text-xs font-medium uppercase tracking-[0.15em] text-slate">
              DIY Video
            </th>
            <th className="pb-3 pl-4 text-center text-xs font-medium uppercase tracking-[0.15em] text-red">
              QuiteRight
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr
              key={i}
              className={`border-b border-stone/50 ${row.highlight ? "bg-cream/50" : ""}`}
            >
              <td className="py-3.5 pr-4 text-sm font-medium text-charcoal">
                {row.label}
              </td>
              <td className="py-3.5 px-4 text-center text-sm text-slate">
                {row.diy}
              </td>
              <td className="py-3.5 pl-4 text-center text-sm font-semibold text-charcoal">
                {row.quiteright}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function FaqAccordion({ items }: { items: FaqItem[] }) {
  return (
    <dl className="divide-y divide-stone">
      {items.map((item, i) => (
        <details key={i} className="group">
          <summary className="flex cursor-pointer items-start justify-between gap-4 py-5 text-left [&::-webkit-details-marker]:hidden">
            <dt className="text-sm font-semibold leading-relaxed text-charcoal">
              {item.q}
            </dt>
            <span className="mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full bg-stone text-slate transition-transform duration-200 group-open:rotate-45">
              <svg
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                className="size-3"
              >
                <path d="M8 3v10M3 8h10" />
              </svg>
            </span>
          </summary>
          <dd className="pb-5 text-sm leading-relaxed text-slate">{item.a}</dd>
        </details>
      ))}
    </dl>
  )
}

/* ------------------------------------------------------------------ */
/*  Page                                                               */
/* ------------------------------------------------------------------ */

export default function QuiterightVsDiyVideo({ meta }: { meta: SeoMeta }) {
  return (
    <PublicLayout>
      <Head>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <link rel="canonical" href={meta.canonical} />

        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:url" content={meta.canonical} />
        <meta property="og:type" content="article" />
        <meta property="og:image" content={meta.image} />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={meta.title} />
        <meta name="twitter:description" content={meta.description} />
        <meta name="twitter:image" content={meta.image} />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: "QuiteRight vs DIY Property Video: Which Wins More Viewings?",
            description: meta.description,
            publisher: {
              "@type": "Organization",
              name: "QuiteRight",
              url: "https://www.quiteright.co.uk",
              logo: {
                "@type": "ImageObject",
                url: "https://www.quiteright.co.uk/qr-logo-wordmark-red.png",
              },
            },
            datePublished: "2026-02-25",
            dateModified: "2026-02-25",
          })}
        </script>

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: FAQ.map((faq) => ({
              "@type": "Question",
              name: faq.q,
              acceptedAnswer: { "@type": "Answer", text: faq.a },
            })),
          })}
        </script>

        <BreadcrumbSchema canonical={meta.canonical} items={[{ name: "QuiteRight vs DIY Video" }]} />
      </Head>

      {/* Hero */}
      <section className="px-6 py-24 md:py-32">
        <div className="mx-auto max-w-6xl">
          <div className="grid items-center gap-12 md:grid-cols-2">
            <div>
              <p className="text-xs font-medium uppercase tracking-[0.2em] text-red">
                Compare
              </p>
              <h1 className="mt-4 font-display text-4xl font-bold tracking-tight text-charcoal sm:text-5xl">
                DIY looks easy until listing five
                <span className="text-red">.</span>
              </h1>
              <p className="mt-6 max-w-lg text-lg text-slate">
                Filming, editing, scripting, adding music, exporting for
                Instagram, TikTok, YouTube, and Facebook. For one listing,
                it&rsquo;s manageable. For twenty? That&rsquo;s a full-time job.
              </p>
              <div className="mt-8 flex flex-col gap-4 sm:flex-row">
                <Link
                  href="/sign-up"
                  className="inline-flex items-center justify-center rounded-md bg-red px-6 py-3 text-base font-medium text-white transition-colors hover:bg-red/90"
                >
                  Start your free trial
                </Link>
                <Link
                  href="/demo"
                  className="inline-flex items-center justify-center rounded-md border border-stone bg-white px-6 py-3 text-base font-medium text-charcoal transition-colors hover:bg-stone"
                >
                  Book a demo
                </Link>
              </div>
            </div>
            <div className="relative flex justify-center">
              <VideoPhone />
              <div className="absolute -bottom-4 -right-4 rounded-lg bg-charcoal px-4 py-2.5 shadow-md md:-right-2">
                <p className="text-xs font-medium text-warm-grey">No filming needed</p>
                <p className="font-display text-xl font-bold text-white">Just paste a URL</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Reality checks */}
      <section className="border-t border-stone bg-cream px-6 py-20">
        <div className="mx-auto max-w-5xl">
          <ScrollReveal>
            <p className="text-center text-xs font-medium uppercase tracking-[0.2em] text-red">
              The reality
            </p>
            <h2 className="mt-4 text-center font-display text-3xl font-bold text-charcoal md:text-4xl">
              What DIY video actually looks like
              <span className="text-red">.</span>
            </h2>
          </ScrollReveal>

          <ScrollReveal stagger className="mt-12 grid gap-6 sm:grid-cols-2">
            {REALITY_CHECKS.map((item, i) => (
              <div key={i} className="rounded-xl border border-stone bg-white p-6">
                <div className="flex items-start gap-3">
                  <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-red/10">
                    <svg viewBox="0 0 20 20" fill="currentColor" className="size-4 text-red">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a.75.75 0 000 1.5h.253a.25.25 0 01.244.304l-.459 2.066A1.75 1.75 0 0010.747 15H11a.75.75 0 000-1.5h-.253a.25.25 0 01-.244-.304l.459-2.066A1.75 1.75 0 009.253 9H9z" clipRule="evenodd" />
                    </svg>
                  </span>
                  <p className="font-display text-lg font-bold text-charcoal">{item.myth}</p>
                </div>
                <p className="mt-3 text-sm leading-relaxed text-slate">{item.reality}</p>
              </div>
            ))}
          </ScrollReveal>
        </div>
      </section>

      {/* Comparison table */}
      <section className="border-t border-stone px-6 py-20">
        <div className="mx-auto max-w-4xl">
          <ScrollReveal>
            <p className="text-center text-xs font-medium uppercase tracking-[0.2em] text-red">
              Side by side
            </p>
            <h2 className="mt-4 text-center font-display text-3xl font-bold text-charcoal md:text-4xl">
              The full comparison
              <span className="text-red">.</span>
            </h2>
          </ScrollReveal>

          <div className="mt-12">
            <ScrollReveal>
              <div className="rounded-xl border border-stone bg-white p-6 md:p-8">
                <ComparisonTable rows={COMPARISON} />
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* The maths */}
      <section className="border-t border-stone bg-charcoal px-6 py-20">
        <div className="mx-auto max-w-4xl">
          <ScrollReveal>
            <p className="text-center text-xs font-medium uppercase tracking-[0.2em] text-red">
              The maths
            </p>
            <h2 className="mt-4 text-center font-display text-3xl font-bold text-white md:text-4xl">
              Your time has a price
              <span className="text-red">.</span>
            </h2>
          </ScrollReveal>

          <div className="mt-12 grid gap-6 sm:grid-cols-3">
            <ScrollReveal>
              <div className="rounded-xl border border-white/10 bg-white/5 p-6 text-center">
                <p className="text-xs font-medium uppercase tracking-[0.15em] text-warm-grey">
                  DIY: 20 listings/month
                </p>
                <p className="mt-3 font-display text-3xl font-bold text-white">
                  40+ hours
                </p>
                <p className="mt-1 text-sm text-warm-grey">of filming and editing</p>
              </div>
            </ScrollReveal>
            <ScrollReveal>
              <div className="rounded-xl border border-red/30 bg-red/10 p-6 text-center">
                <p className="text-xs font-medium uppercase tracking-[0.15em] text-red">
                  QuiteRight: 20 listings/month
                </p>
                <p className="mt-3 font-display text-3xl font-bold text-white">
                  20 minutes
                </p>
                <p className="mt-1 text-sm text-warm-grey">paste, pick, publish</p>
              </div>
            </ScrollReveal>
            <ScrollReveal>
              <div className="rounded-xl border border-white/10 bg-white/5 p-6 text-center">
                <p className="text-xs font-medium uppercase tracking-[0.15em] text-warm-grey">
                  Time saved
                </p>
                <p className="mt-3 font-display text-3xl font-bold text-red">
                  39+ hours
                </p>
                <p className="mt-1 text-sm text-warm-grey">every single month</p>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Visual break: the result */}
      <section className="border-t border-stone bg-cream px-6 py-20">
        <div className="mx-auto max-w-5xl">
          <ScrollReveal>
            <div className="grid items-center gap-8 md:grid-cols-2">
              <div className="overflow-hidden rounded-xl shadow-lg">
                <OptimizedImage
                  src={cdnUrl("/property-merchants-road-800w.webp")}
                  srcSet={imageSrcSet("/property-merchants-road-800w.webp")}
                  sizes="(max-width: 768px) 100vw, 50vw"
                  alt="Professional property video ad created with QuiteRight"
                  className="w-full"
                  width={800}
                  height={600}
                />
              </div>
              <div>
                <p className="text-xs font-medium uppercase tracking-[0.2em] text-red">
                  The result
                </p>
                <h2 className="mt-4 font-display text-3xl font-bold text-charcoal md:text-4xl">
                  Professional, every time
                  <span className="text-red">.</span>
                </h2>
                <p className="mt-4 text-base leading-relaxed text-slate">
                  No shaky footage, no awkward lighting, no inconsistent branding.
                  Every QuiteRight video meets the same professional standard,
                  whether it&rsquo;s your first listing or your fiftieth.
                </p>
                <ul className="mt-6 space-y-3">
                  {[
                    "AI voiceover with multiple voice options",
                    "Royalty-free music, pre-selected",
                    "Your logo and branding on every video",
                    "All social formats included automatically",
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm text-slate">
                      <svg viewBox="0 0 20 20" fill="currentColor" className="mt-0.5 size-4 shrink-0 text-red">
                        <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clipRule="evenodd" />
                      </svg>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-stone px-6 py-20">
        <div className="mx-auto max-w-3xl">
          <ScrollReveal>
            <p className="text-center text-xs font-medium uppercase tracking-[0.2em] text-red">
              FAQ
            </p>
            <h2 className="mt-4 text-center font-display text-3xl font-bold text-charcoal md:text-4xl">
              Common questions
              <span className="text-red">.</span>
            </h2>
          </ScrollReveal>

          <div className="mt-12">
            <ScrollReveal>
              <FaqAccordion items={FAQ} />
            </ScrollReveal>
          </div>
        </div>
      </section>

      <RelatedPages links={relatedFor("quiteright-vs-diy-video")} tone="white" />

      {/* CTA */}
      <section className="border-t border-stone bg-cream px-6 py-24">
        <div className="mx-auto max-w-3xl text-center">
          <ScrollReveal>
            <h2 className="font-display text-3xl font-bold text-charcoal md:text-4xl">
              Stop filming. Start marketing
              <span className="text-red">.</span>
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-lg text-slate">
              Every listing deserves a video. QuiteRight makes it possible
              without a camera, a microphone, or a free afternoon.
            </p>
            <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
              <Link
                href="/sign-up"
                className="inline-flex w-full items-center justify-center rounded-md bg-red px-6 py-3 text-base font-medium text-white transition-colors hover:bg-red/90 sm:w-auto"
              >
                Start your free trial
              </Link>
              <Link
                href="/demo"
                className="inline-flex w-full items-center justify-center rounded-md border border-stone bg-white px-6 py-3 text-base font-medium text-charcoal transition-colors hover:bg-stone sm:w-auto"
              >
                Book a demo
              </Link>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </PublicLayout>
  )
}
